import { useState, useEffect, useCallback, useRef } from "react";
import axios from "axios";
import { API, CAROUSEL_INTERVAL_MS } from "@/lib/constants";

export const SitePhotosCarousel = () => {
  const [photos, setPhotos] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const timerRef = useRef(null);

  useEffect(() => {
    const fetchPhotos = async () => {
      try {
        const res = await axios.get(`${API}/site-photos`);
        setPhotos(res.data || []);
      } catch (err) {
        console.error("Failed to fetch site photos:", err);
      }
    };
    fetchPhotos();
  }, []);

  const startTimer = useCallback(() => {
    if (timerRef.current) clearInterval(timerRef.current);
    timerRef.current = setInterval(() => {
      setCurrentIndex((prev) => (prev + 1) % photos.length);
    }, CAROUSEL_INTERVAL_MS);
  }, [photos.length]);

  useEffect(() => {
    if (photos.length <= 1) return;
    startTimer();
    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
    };
  }, [photos.length, startTimer]);

  const goTo = (idx) => {
    setCurrentIndex(idx);
    startTimer();
  };

  if (photos.length === 0) return null;

  return (
    <section id="site-gallery" className="py-20 bg-white" data-testid="site-gallery-section">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-6">
          <p
            className="text-sm uppercase tracking-[0.4em] text-orange-500"
            data-testid="site-gallery-eyebrow"
          >
            On-Site Progress
          </p>
          <h2
            className="font-heading text-4xl md:text-5xl uppercase mt-4 text-[#003366]"
            data-testid="site-gallery-title"
          >
            Current Project Updates
          </h2>
        </div>

        <div className="relative overflow-hidden" data-testid="site-gallery-carousel">
          <div className="relative w-full" style={{ paddingBottom: "56.25%" }}>
            {photos.map((photo, idx) => (
              <div
                key={photo.id}
                className="absolute inset-0 transition-opacity duration-1000 ease-in-out"
                style={{
                  opacity: idx === currentIndex ? 1 : 0,
                  zIndex: idx === currentIndex ? 1 : 0,
                }}
                data-testid={`site-gallery-slide-${idx}`}
              >
                <img
                  loading="lazy"
                  src={`${API}/site-photos/${photo.id}/file`}
                  alt={photo.caption || `Project site ${idx + 1}`}
                  className="absolute inset-0 w-full h-full object-cover"
                />
                {photo.caption && (
                  <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/70 to-transparent p-6">
                    <p
                      className="text-white text-lg font-medium"
                      data-testid={`site-gallery-caption-${idx}`}
                    >
                      {photo.caption}
                    </p>
                  </div>
                )}
              </div>
            ))}
          </div>

          {photos.length > 1 && (
            <>
              <button
                className="absolute left-3 top-1/2 -translate-y-1/2 z-10 w-10 h-10 bg-white/80 hover:bg-white text-[#003366] flex items-center justify-center shadow-md transition-colors"
                onClick={() => goTo((currentIndex - 1 + photos.length) % photos.length)}
                data-testid="site-gallery-prev"
              >
                &#8249;
              </button>
              <button
                className="absolute right-3 top-1/2 -translate-y-1/2 z-10 w-10 h-10 bg-white/80 hover:bg-white text-[#003366] flex items-center justify-center shadow-md transition-colors"
                onClick={() => goTo((currentIndex + 1) % photos.length)}
                data-testid="site-gallery-next"
              >
                &#8250;
              </button>
            </>
          )}
        </div>

        {photos.length > 1 && (
          <div className="flex justify-center gap-2 mt-6" data-testid="site-gallery-dots">
            {photos.map((photo, idx) => (
              <button
                key={photo.id}
                className={`w-3 h-3 rounded-full transition-colors ${
                  idx === currentIndex ? "bg-[#003366]" : "bg-slate-300"
                }`}
                onClick={() => goTo(idx)}
                data-testid={`site-gallery-dot-${idx}`}
              />
            ))}
          </div>
        )}
      </div>
    </section>
  );
};
